import React, { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { RhythmScoreBadge, YatSprite } from '@pkg/components';
import { useAppDispatch, useAppSelector, useUpdateUser } from '@pkg/hooks';
import { groupsCacheKeys } from '@pkg/hooks/deprecated';

import { type SidebarYat } from '@app/helpers';
import { setIsExpanded } from '@app/store/actions/dashboard';
import { addNotification } from '@app/store/actions/notification';
import { setPrimaryYat } from '@app/store/actions/user';
import { setActiveEmojiId } from '@app/store/actions/user-emojis';
import { getYatPageDetails } from '@app/store/actions/yat-page';
import { useAuthTokens } from '@app/store/zustand/Auth/useUserTokens';

import './Item.scss';

interface YatSidebarItemProps {
    yat: SidebarYat;
    isActive?: boolean;
    isPrimaryYatPicker?: boolean;
    onClick?(): void;
}

const YatSidebarItem = ({ yat, isActive = false, isPrimaryYatPicker = false, onClick }: YatSidebarItemProps) => {
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();
    const { sidebar } = useAppSelector((state) => state.dashboard);
    const isAuthenticated = useAuthTokens((state) => state.isAuthenticated);
    const { mutateAsync: updateUser } = useUpdateUser();
    const { t } = useTranslation('primary_yat');

    const handlePrimaryYat = useCallback(
        async (yatString) => {
            updateUser({ value: { eid: yatString, confirm_eid: true }, dispatch }).then((r) => {
                if (r.ok && r.value) {
                    dispatch(setPrimaryYat(yatString));
                    dispatch(
                        addNotification({
                            message: t('yat_successfully_updated'),
                            duration: 6000,
                            variant: 'primary',
                            emoji: '🖖',
                        }),
                    );
                    queryClient.invalidateQueries(groupsCacheKeys.groupsKey);
                    queryClient.invalidateQueries(groupsCacheKeys.groupAll());
                }
            });
        },
        [dispatch, queryClient, t, updateUser],
    );

    const handleActiveYat = useCallback(
        async (yatString) => {
            await dispatch(setActiveEmojiId(yatString));
        },
        [dispatch],
    );

    const handleSelectedDone = useCallback(
        (yatString) => {
            dispatch(getYatPageDetails(yatString)).then(() => {
                sidebar.closeOpenGroup();
                dispatch(setIsExpanded(false));
                onClick?.();
            });
        },
        [dispatch, onClick, sidebar],
    );

    const onSelected = useCallback(() => {
        const yatString = yat.toString();
        if (!isAuthenticated || !yatString) return;
        if (isPrimaryYatPicker) {
            handlePrimaryYat(yatString).then(() => {
                handleSelectedDone(yatString);
                return;
            });
        } else {
            handleActiveYat(yatString).then(() => {
                handleSelectedDone(yatString);
                return;
            });
        }
    }, [handleActiveYat, handlePrimaryYat, handleSelectedDone, isAuthenticated, isPrimaryYatPicker, yat]);

    const rhythmScoreMarkup = yat.rhythmScore ? (
        <RhythmScoreBadge score={yat.rhythmScore} classes="yat-sidebar-item__rhythm-score-badge" hasPadding={false} />
    ) : null;

    return (
        <div
            role="button"
            tabIndex={0}
            className={`v2-global-reset yat-sidebar-item${isActive ? ' yat-sidebar-item--is-active' : ''}`}
            onClick={onSelected}
        >
            <div className="yat-sidebar-item__background" />

            <div className="yat-sidebar-item__inner">
                <span className="yat-sidebar-item__first-char">
                    {yat.firstChar && <YatSprite emojiID={yat.firstChar} />}
                </span>
                <span className="yat-sidebar-item__remaining-chars">
                    {yat.trailingChars && <YatSprite emojiID={yat.trailingChars} />}
                </span>
            </div>

            <div className="yat-sidebar-item__length-indicator" aria-hidden="true">
                {(yat.emojis || []).map((_, i) => (
                    <span key={i} />
                ))}
            </div>

            {rhythmScoreMarkup}
        </div>
    );
};

export default YatSidebarItem;
